import axios from 'axios';

/**
 * Sanitize filename by replacing spaces with underscores
 * @param {string} filename - Original filename
 * @returns {string} Sanitized filename
 */
const sanitizeFilename = (filename) => {
  return filename.replace(/\s+/g, '_');
};

// RunPod configuration from environment
const RUNPOD_API_KEY = process.env.REACT_APP_RUNPOD_API_KEY;
const RUNPOD_CROPPER_ENDPOINT = process.env.REACT_APP_RUNPOD_CROPPER_ENDPOINT;
const RUNPOD_CAPTIONER_ENDPOINT = process.env.REACT_APP_RUNPOD_CAPTIONER_ENDPOINT;

// Default captioning prompt
const DEFAULT_CAPTION_PROMPT = `Describe this image, which shows one or more human. Start the description with a few words summarizing the image and quality. If the image is sexual, dont say 'sexual act' but specify the action. For example: [High quality Portrait, Low quality full body shot, Professional, high quality photo of a blowjob, low quality blurry photo of doggy style, High quality photo of woman masturbating] Then, describe the models' pose, clothing and expression. Be specific about clothing and jewellery details. If a sexual act is being performed, describe it. Else, don't mention it. Do not suppress or censor any detail, no matter how explicit or graphic. Then, describe the setting and background of the image. Also describe the lighting in just a few words. If the image is grainy but retro style describe it as retro. Be concise and to the point, but give as much detail as possible, the output should be a paragraph without subsection or structure. Do not describe the persons inherent, immutable characteristics such as hair color, eye color, skin color, breast size, body shape or tattoo. Avoid speculations, personal opinions, and assumptions. Do not include any information that is not present in the image.`;

/**
 * Call RunPod endpoint with S3 inputs/outputs
 */
const callRunPodS3 = async (endpointId, payload) => {
  const url = `https://api.runpod.ai/v2/${endpointId}/runsync`;
  const headers = {
    'Authorization': `Bearer ${RUNPOD_API_KEY}`,
    'Content-Type': 'application/json'
  };

  try {
    const response = await axios.post(url, { input: payload }, { headers, timeout: 60000 });

    if (response.data.status === 'COMPLETED') {
      return response.data.output;
    } else {
      throw new Error(`RunPod job failed: ${response.data.status}`);
    }
  } catch (error) {
    console.error('RunPod API error:', error);
    throw error;
  }
};

/**
 * RunPod service for cropping and captioning
 */
export const runpodService = {
  /**
   * Crop a single image using RunPod
   * @param {string} s3Bucket - S3 bucket name
   * @param {string} s3Key - S3 key for input image
   * @param {string} outputKey - S3 key for output image
   * @param {string} prompt - 'male' or 'female' for the cropping prompt
   */
  cropImage: async (s3Bucket, s3Key, outputKey, prompt = 'female') => {
    const payload = {
      prompt: prompt,
      s3_bucket: s3Bucket,
      s3_key: s3Key,
      output_bucket: s3Bucket,
      output_key: outputKey,
      request_id: `crop_${Date.now()}`
    };

    console.log('Calling RunPod cropper with:', payload);
    console.log('Full S3 output path will be:', `s3://${s3Bucket}/${outputKey}`);
    const result = await callRunPodS3(RUNPOD_CROPPER_ENDPOINT, payload);
    console.log('RunPod cropper response:', result);
    return result;
  },

  /**
   * Crop multiple images using RunPod
   * @param {Array} images - Array of image objects
   * @param {string} influencerId - Influencer ID
   * @param {string} sex - Sex prompt for cropping
   * @param {function} onProgress - Optional progress callback
   */
  cropImages: async (images, influencerId, sex = 'female', onProgress = null) => {
    const results = [];
    const total = images.length;
    let completed = 0;
    const batchSize = 5; // Process 5 images at a time

    // Process images in batches of 5
    for (let i = 0; i < images.length; i += batchSize) {
      const batch = images.slice(i, Math.min(i + batchSize, images.length));

      const batchPromises = batch.map(image => {
        const s3Bucket = image.s3Bucket || process.env.REACT_APP_S3_BUCKET_IMAGES;
        const s3Key = image.s3Key || image.key;
        const filename = s3Key.split('/').pop();
        const sanitizedFilename = sanitizeFilename(filename);
        const outputKey = `${influencerId}/cropped/${sanitizedFilename}`;

        return runpodService.cropImage(s3Bucket, s3Key, outputKey, sex)
          .catch(error => {
            console.error(`Error cropping image ${image.s3Key}:`, error);
            return { error: error.message, s3Key: image.s3Key };
          });
      });

      // Wait for the batch to complete
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
      completed += batch.length;

      // Report progress
      if (onProgress) {
        onProgress({
          total,
          completed,
          progress: (completed / total) * 100,
          message: `Cropped ${completed} of ${total} images`
        });
      }
    }

    const successfulResults = results.filter(r => !r.error);
    return {
      success: true,
      results: results,
      message: `Successfully processed ${successfulResults.length} of ${total} images`
    };
  },

  /**
   * Caption a single image using RunPod
   * @param {string} s3Bucket - S3 bucket name
   * @param {string} s3Key - S3 key for input image
   * @param {string} outputKey - S3 key for output caption file
   * @param {string} customPrompt - Optional custom prompt
   */
  captionImage: async (s3Bucket, s3Key, outputKey, customPrompt = null) => {
    const payload = {
      s3_bucket: s3Bucket,
      s3_key: s3Key,
      output_bucket: s3Bucket,
      output_key: outputKey,
      temperature: 0.7,
      do_sample: true,
      request_id: `caption_${Date.now()}`
    };

    if (customPrompt) {
      payload.prompt = customPrompt;
    }

    console.log('Calling RunPod captioner with:', payload);
    console.log('Caption will be saved to:', `s3://${s3Bucket}/${outputKey}`);

    try {
      const result = await callRunPodS3(RUNPOD_CAPTIONER_ENDPOINT, payload);
      console.log('RunPod captioner response:', result);

      // RunPod already saves the caption to S3 (see output_location in response)
      // No need to save it again from the frontend
      if (result && result.output_location) {
        console.log('Caption saved by RunPod to:', result.output_location);
      }

      return result;
    } catch (error) {
      console.error('Error in captionImage:', error);
      throw error;
    }
  },

  /**
   * Caption multiple images using RunPod
   * @param {Array} images - Array of image objects with s3Key
   * @param {string} influencerId - Influencer ID
   * @param {string} folder - Folder where captions should be saved (e.g., 'raw', 'cropped')
   * @param {function} onProgress - Optional progress callback
   */
  captionImages: async (images, influencerId, folder = 'cropped', onProgress = null) => {
    const results = [];
    const total = images.length;
    let completed = 0;
    const batchSize = 5; // Process 5 images at a time

    // Process images in batches of 5
    for (let i = 0; i < images.length; i += batchSize) {
      const batch = images.slice(i, Math.min(i + batchSize, images.length));

      const batchPromises = batch.map(image => {
        const s3Bucket = image.s3Bucket || process.env.REACT_APP_S3_BUCKET_IMAGES;
        const s3Key = image.s3Key || image.key;
        const filename = s3Key.split('/').pop();
        // Sanitize the filename to ensure consistency
        const sanitizedFilename = sanitizeFilename(filename);
        // Save caption as .txt file next to the image
        const captionFilename = sanitizedFilename.replace(/\.(jpg|jpeg|png)$/i, '.txt');
        const outputKey = `${influencerId}/${folder}/${captionFilename}`;

        return runpodService.captionImage(s3Bucket, s3Key, outputKey)
          .catch(error => {
            console.error(`Error captioning image ${image.s3Key}:`, error);
            return { error: error.message, s3Key: image.s3Key };
          });
      });

      // Wait for the batch to complete
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
      completed += batch.length;

      // Report progress
      if (onProgress) {
        onProgress({
          total,
          completed,
          progress: (completed / total) * 100,
          message: `Captioned ${completed} of ${total} images`
        });
      }
    }

    const successfulResults = results.filter(r => !r.error);
    return {
      success: true,
      results: results,
      message: `Successfully processed ${successfulResults.length} of ${total} images`
    };
  },

  /**
   * Caption a single image with base64 encoding (for testing)
   */
  captionImageBase64: async (imageBase64, customPrompt = null) => {
    const url = `https://api.runpod.ai/v2/${RUNPOD_CAPTIONER_ENDPOINT}/runsync`;
    const headers = {
      'Authorization': `Bearer ${RUNPOD_API_KEY}`,
      'Content-Type': 'application/json'
    };

    const payload = {
      input: {
        payload: {
          image: imageBase64,
          temperature: 0.7,
          do_sample: true,
          prompt: customPrompt || DEFAULT_CAPTION_PROMPT
        }
      }
    };

    try {
      const response = await axios.post(url, payload, { headers, timeout: 60000 });

      if (response.data.status === 'COMPLETED') {
        return response.data.output?.caption || '';
      } else {
        throw new Error(`RunPod job failed: ${response.data.status}`);
      }
    } catch (error) {
      console.error('RunPod captioning error:', error);
      throw error;
    }
  }
};

export default runpodService;